import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { validateImageFile, createFilePreview } from '../../services/uploadAPI';
import './LogoUpload.css';

/**
 * Reusable Logo Upload Component
 * Supports drag & drop, file preview, and upload progress
 */
const LogoUpload = ({
    currentLogo,
    onUpload,
    title = "Logo",
    maxSizeMB = 5,
    aspectRatio = "square", // 'square', 'wide', 'custom'
    showPreview = true
}) => {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(currentLogo || null);
    const [dragActive, setDragActive] = useState(false);

    const handleFileSelect = async (file) => {
        if (!file) return;

        // Validate file
        const validation = validateImageFile(file, maxSizeMB);
        if (!validation.valid) {
            toast.error(validation.error);
            return;
        }

        try {
            // Create preview
            const previewUrl = await createFilePreview(file);
            setPreview(previewUrl);

            // Upload file
            setUploading(true);
            await onUpload(file);
            toast.success(`${title} uploaded successfully!`);
        } catch (error) {
            toast.error(`Failed to upload ${title.toLowerCase()}`);
            setPreview(currentLogo); // Reset to current logo on error
        } finally {
            setUploading(false);
        }
    };

    const handleInputChange = (e) => {
        const file = e.target.files[0];
        handleFileSelect(file);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    return (
        <div className="logo-upload-container">
            <h4 className="logo-upload-title">{title}</h4>

            <div
                className={`logo-upload-area ${dragActive ? 'drag-active' : ''} ${aspectRatio}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
            >
                {showPreview && preview ? (
                    <div className="logo-preview">
                        <img src={preview} alt={title} className="logo-preview-img" />
                        <div className="logo-preview-overlay">
                            <label className="btn-primary-modern btn-sm">
                                <i className="fas fa-upload"></i>
                                Change {title}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleInputChange}
                                    disabled={uploading}
                                />
                            </label>
                        </div>
                    </div>
                ) : (
                    <div className="logo-placeholder">
                        <i className="fas fa-image fa-3x mb-3 text-muted"></i>
                        <p className="text-muted mb-3">
                            Drag & drop your {title.toLowerCase()} here or
                        </p>
                        <label className="btn-primary-modern">
                            <i className="fas fa-upload"></i>
                            {uploading ? 'Uploading...' : `Choose ${title}`}
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleInputChange}
                                disabled={uploading}
                            />
                        </label>
                        <p className="text-sm text-muted mt-3">
                            Max file size: {maxSizeMB}MB
                        </p>
                        <p className="text-xs text-muted">
                            Supported: JPG, PNG, GIF, SVG
                        </p>
                    </div>
                )}
            </div>

            {uploading && (
                <div className="upload-progress">
                    <div className="spinner-small"></div>
                    <span>Uploading...</span>
                </div>
            )}
        </div>
    );
};

export default LogoUpload;
